import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PatientHeader = ({ patient, onEditPatient, onViewCredentials }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-success text-success-foreground';
      case 'Inactive': return 'bg-muted text-muted-foreground';
      case 'Critical': return 'bg-error text-error-foreground';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today?.getFullYear() - birth?.getFullYear();
    const monthDiff = today?.getMonth() - birth?.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birth?.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-clinical">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        {/* Patient Info */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative">
            <Image
              src={patient?.avatar}
              alt={`${patient?.name} profile`}
              className="w-20 h-20 rounded-full object-cover border-2 border-border"
            />
            <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-card flex items-center justify-center ${getStatusColor(patient?.status)}`}>
              <Icon name="User" size={12} />
            </div>
          </div>
          
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
              <h1 className="text-2xl font-semibold text-foreground">{patient?.name}</h1>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(patient?.status)}`}>
                {patient?.status}
              </span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Patient ID:</span>
                <p className="font-medium text-foreground">{patient?.id}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Age:</span>
                <p className="font-medium text-foreground">{calculateAge(patient?.dateOfBirth)} years</p>
              </div>
              <div>
                <span className="text-muted-foreground">Gender:</span>
                <p className="font-medium text-foreground">{patient?.gender}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Blood Type:</span>
                <p className="font-medium text-foreground">{patient?.bloodType}</p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
              <div className="flex items-center gap-1 text-muted-foreground">
                <Icon name="Phone" size={14} />
                <span>{patient?.phone}</span>
              </div>
              <div className="flex items-center gap-1 text-muted-foreground">
                <Icon name="Mail" size={14} />
                <span>{patient?.email}</span>
              </div>
              <div className="flex items-center gap-1 text-muted-foreground">
                <Icon name="MapPin" size={14} />
                <span>{patient?.address}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" iconName="Shield" iconPosition="left" onClick={onViewCredentials}>
            View Credentials
          </Button>
          <Button variant="default" iconName="Edit" iconPosition="left" onClick={onEditPatient}>
            Edit Patient
          </Button>
        </div>
      </div>
      {/* Emergency Contact & Insurance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 pt-6 border-t border-border">
        <div>
          <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
            <Icon name="Phone" size={16} />
            Emergency Contact
          </h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Name:</span>
              <span className="ml-2 font-medium text-foreground">{patient?.emergencyContact?.name}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Relationship:</span>
              <span className="ml-2 font-medium text-foreground">{patient?.emergencyContact?.relationship}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Phone:</span>
              <span className="ml-2 font-medium text-foreground">{patient?.emergencyContact?.phone}</span>
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
            <Icon name="Shield" size={16} />
            Insurance Information
          </h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Provider:</span>
              <span className="ml-2 font-medium text-foreground">{patient?.insurance?.provider}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Policy Number:</span>
              <span className="ml-2 font-medium text-foreground">{patient?.insurance?.policyNumber}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Group:</span>
              <span className="ml-2 font-medium text-foreground">{patient?.insurance?.group}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientHeader;